import { useCallback, useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { Button } from '@context365/button'
import {
  ContentPasteOutlined,
  EventNoteOutlined,
  GroupsOutlined,
  PeopleOutlined,
} from '@context365/icons'
import { TabButton, TabList, TabPanel, Tabs } from '@context365/tabs'
import {
  faCalendar,
  faHandshake,
  faPiggyBank,
} from '@fortawesome/pro-regular-svg-icons'
import { faMapMarkerAlt } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import filter from 'lodash/filter'
import get from 'lodash/get'
import isEmpty from 'lodash/isEmpty'
import join from 'lodash/join'
import map from 'lodash/map'
import moment from 'moment'
import { Link, useRouteMatch } from 'react-router-dom'
import { getEventFunds } from '~/actions/fundList'
import Agenda from '~/components/Agenda'
import TieFundsModal from '~/components/TieFundsModal/TieFundsModal'
import AttendeeList from './attendees/AttendeeList'
import EventMeetingsTab from './meetings/EventMeetingsTab'
import Schedule from './Schedule'
import './SummitDetails.less'

const SummitDetails = ({ events, selectedEvent, role, subtab = '' }) => {
  const {
    params: { id, tab },
  } = useRouteMatch()
  const selectedEventId = parseInt(id, 10)
  const isFundManager = role.toLowerCase() === 'manager'
  const filteredEvents = events.filter(
    (item) => item.eventId === selectedEventId
  )
  const event = filteredEvents[0]
  const { schedulerAccessDate } = isEmpty(filteredEvents)
    ? { schedulerAccessDate: '' }
    : filteredEvents[0]

  const rolesAllowedToBookSeats = ['manager', 'service provider']
  const [isModalVisible, setIsModalVisible] = useState(false)

  const locationKeys = [
    'venue',
    'address1',
    'address2',
    'address3',
    'city',
    'state',
    'postalCode',
    'country',
  ]

  const address = join(
    map(
      filter(locationKeys, (key) => !isEmpty(get(event, key))),
      (key) => get(event, key)
    ),
    ', '
  )

  const startDateMoment = moment(event.startDate, moment.ISO_8601)
  const endDateMoment = moment(event.endDate, moment.ISO_8601)
  const eventDates =
    startDateMoment.month === endDateMoment.month
      ? startDateMoment.format('MMMM D') +
        ' - ' +
        endDateMoment.format('D, YYYY')
      : startDateMoment.format('MMMM D, YYYY') +
        ' - ' +
        endDateMoment.format('MMMM D, YYYYY')

  const booths = event.booths ? (
    event.booths.map((booth) => (
      <span className="mr-1" key="{booth.boothId}">
        {booth.boothName}
      </span>
    ))
  ) : (
    <span>TBD</span>
  )

  const funds = isEmpty(event.funds) ? (
    <span />
  ) : (
    <span>
      {event.funds.map(
        (fund, key) =>
          key < 3 && (
            <span key={key} style={{ marginLeft: '5px' }}>
              {fund.name}
              {key !== event.funds.length - 1 && <>,</>}
            </span>
          )
      )}
      <span />
      {event.funds.length > 3 && <span>...</span>}
    </span>
  )

  const fundSelection = event?.package?.canAddFund ? (
    isEmpty(event.funds) ? (
      <span className="m-1">
        <span style={{ color: '#ff2020' }} className="mr-2">
          Funds Required
        </span>
        <Button
          variant="outlined"
          size="small"
          onClick={() => setIsModalVisible(true)}
        >
          Add Funds
        </Button>
      </span>
    ) : (
      <Button variant="link" onClick={() => setIsModalVisible(true)}>
        Add Funds
      </Button>
    )
  ) : null

  const [attendeeAccess, setAttendeeAccess] = useState(true)
  const [defaultTab] = useState(tab || 'attendees')
  const [refresh, setRefresh] = useState(false)

  const refreshFunds = useCallback(() => {
    if (isFundManager) {
      getEventFunds(selectedEvent.eventId).then((response) => {
        setAttendeeAccess(response.data.result.length > 0) //cannot access the list if you dont have funds tied to the event
      })
    } else setAttendeeAccess(true)
  }, [isFundManager, selectedEvent.eventId])

  useEffect(() => {
    refreshFunds()
  }, [selectedEvent, isFundManager, refresh, refreshFunds])

  const useBreakpoint = () => {
    const [breakpoint, setBreakpoint] = useState('')

    const handleResize = () => {
      const width = window.innerWidth
      if (width < 600) setBreakpoint('xs')
      else if (width < 900) setBreakpoint('sm')
      else if (width < 1200) setBreakpoint('md')
      else if (width < 1536) setBreakpoint('lg')
      else setBreakpoint('xl')
    }

    useEffect(() => {
      handleResize()
      window.addEventListener('resize', handleResize)
      return () => window.removeEventListener('resize', handleResize)
    }, [])

    return breakpoint
  }

  // Make Tabs mobile friendly
  const breakpoint = useBreakpoint()
  let tabSize = 'big'
  if (breakpoint === 'xs') {
    tabSize = 'big'
  }

  return (
    <div className="SummitDetails">
      <div className="type-subtitle-md px-4 py-2 bg-transparent md:p-0 md:bg-transparent">
        {selectedEvent.name}
      </div>
      <div className="type-body-regular-xs text-primary-100 px-4 md:p-0">
        <span className="whitespace-nowrap leading-6">
          <FontAwesomeIcon color="gray" icon={faCalendar} />
          <span className="mx-2">{eventDates}</span>
        </span>
        {address && (
          <span className="whitespace-nowrap leading-6">
            <FontAwesomeIcon color="gray" icon={faMapMarkerAlt} />
            <span className="mx-2">{address}</span>
          </span>
        )}
        {!isEmpty(event.participantsFromMyCompany) &&
          !event.packageName?.toLowerCase().includes('networking') &&
          rolesAllowedToBookSeats.includes(role.toLowerCase()) && (
            <span className="whitespace-nowrap leading-6">
              <FontAwesomeIcon
                className="w-4"
                color="gray"
                icon={faHandshake}
              />
              <span className="mx-1">Booth: {booths}</span>
            </span>
          )}
        {role.toLowerCase() === 'manager' &&
          !isEmpty(event.participantsFromMyCompany) && (
            <span className="whitespace-nowrap leading-6">
              <FontAwesomeIcon
                className="w-4"
                color="gray"
                icon={faPiggyBank}
              />
              <span className="mx-1">Funds: {funds}</span>
              <span className="hidden md:inline">{fundSelection}</span>
            </span>
          )}
      </div>
      <Tabs
        className="w-full max-w-full bg-white m-0 p-0 pb-2 pt-1"
        selectedTab={defaultTab}
      >
        <TabList
          className="overflow-x-auto md:bg-transparent md:px-0 text-sm md:text-base"
          variant="underlined"
          size={tabSize}
        >
          <TabButton
            id="attendees"
            as={Link}
            to={`/summits/${selectedEvent.eventId}/attendees`}
            iconLeft={<GroupsOutlined />}
            label="Attendees"
          />
          <TabButton
            id="meetings"
            as={Link}
            to={`/summits/${selectedEvent.eventId}/meetings`}
            iconLeft={<PeopleOutlined />}
            label="Meetings"
          />
          {selectedEvent.amRegistered && (
            <TabButton
              id="schedule"
              as={Link}
              to={`/summits/${selectedEvent.eventId}/schedule`}
              iconLeft={<EventNoteOutlined />}
              label="Schedule"
            />
          )}
          {breakpoint !== 'xs' && (
            <TabButton
              id="agenda"
              className="hidden"
              as={Link}
              to={`/summits/${selectedEvent.eventId}/agenda`}
              iconLeft={<ContentPasteOutlined />}
              label="Agenda"
            />
          )}
        </TabList>
        <TabPanel tabId="attendees" className="h-full flex flex-col">
          {() => (
            <AttendeeList
              attendeeAccess={attendeeAccess}
              schedulerAccessDate={schedulerAccessDate}
              eventId={selectedEvent.eventId}
              // companyEventId={selectedEvent.companyEventId}
              isOptimized={selectedEvent.isOptimized}
              role={role}
              creditsAvailable={selectedEvent.creditsAvailable}
              contextPoints={selectedEvent.contextPoints}
              subtab={subtab}
              onRefresh={() => setRefresh(!refresh)}
              confirmedMeetingLimit={selectedEvent.confirmedMeetingsLimit}
              confirmedMeetings={selectedEvent.confirmedMeetings}
              disabledMeetingRequest={selectedEvent.disabledMeetingRequest}
              boothSP={selectedEvent.booths}
            />
          )}
        </TabPanel>
        <TabPanel tabId="meetings">
          {() => (
            <EventMeetingsTab
              confirmedMeetingLimit={selectedEvent.confirmedMeetingsLimit}
              confirmedMeetings={selectedEvent.confirmedMeetings}
              disabledMeetingRequest={selectedEvent.disabledMeetingRequest}
              eventId={selectedEvent.eventId}
              eventStartDate={selectedEvent.startDate}
              tab={tab}
              subtab={subtab}
              path={`/summits/${selectedEvent.eventId}/`}
            />
          )}
        </TabPanel>
        {selectedEvent.amRegistered && (
          <TabPanel tabId="schedule">
            {() => (
              <Schedule
                eventId={selectedEvent.eventId}
                //companyEventId={selectedEvent.companyEventId}
                defaultTimezone={selectedEvent.timeZone}
                virtualSummit={selectedEvent.virtualSummit}
                optimizationDate={selectedEvent.optimizationDate}
                agendaUrl={selectedEvent?.agendaUrl}
              />
            )}
          </TabPanel>
        )}
        <TabPanel tabId="agenda">
          {() => <Agenda eventId={selectedEvent.eventId} />}
        </TabPanel>
      </Tabs>
      <TieFundsModal
        eventId={event.eventId}
        isModalVisible={isModalVisible}
        onClose={() => setIsModalVisible(false)}
        onRefresh={() => refreshFunds()}
        initialFunds={event.funds}
      />
    </div>
  )
}

SummitDetails.propTypes = {
  events: PropTypes.arrayOf(PropTypes.any),
  selectedEvent: PropTypes.shape({
    eventId: PropTypes.number.isRequired,
    // companyEventId: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    isOptimized: PropTypes.bool.isRequired,
    schedulerAccessDate: PropTypes.instanceOf(Date),
    optimizationDate: PropTypes.instanceOf(Date),
    startDate: PropTypes.instanceOf(Date),
    endDate: PropTypes.instanceOf(Date),
    creditsAvailable: PropTypes.number,
    contextPoints: PropTypes.number.isRequired,
    timeZone: PropTypes.string.isRequired,
    confirmedMeetingsLimit: PropTypes.number.isRequired,
    confirmedMeetings: PropTypes.number.isRequired,
    disabledMeetingRequest: PropTypes.bool.isRequired,
    virtualSummit: PropTypes.bool.isRequired,
    amRegistered: PropTypes.bool.isRequired,
    booths: PropTypes.array,
    agendaUrl: PropTypes.string,
  }).isRequired,
  role: PropTypes.string.isRequired,
  subtab: PropTypes.string,
}

export default SummitDetails
